// 直式遮罩 — 偵測裝置方向，直式時顯示「請旋轉」覆蓋
// 對應 PRD §3.2.7 Orientation、§16.1 風險（iOS Safari 不允許 lock orientation）
//
// === 使用圖片 ===
// 無 — DOM / CSS 綠金背景、綠色面板與手機輪廓，零 asset 依賴。
//
// 設計策略：
// 1. 嘗試 screen.orientation.lock('landscape')（需 fullscreen 才生效）
// 2. 若 lock 失敗或被瀏覽器拒絕，fallback 到 DOM overlay 遮罩
// 3. 監聽 orientationchange / resize 事件動態顯示/隱藏遮罩
//
// 為什麼用 DOM overlay 而非 Phaser Scene？
// - 遮罩需要在 Phaser canvas 載入失敗時也能顯示
// - DOM overlay 可以蓋過所有 Phaser 內容，確保使用者看不到下方
// - 純 CSS + 一個 div，零依賴，啟動快

import { ZH_TW } from '@i18n/zh-tw';

const ORIENTATION_STYLE = {
  background: 'linear-gradient(#2a8c3a, #0a3d2e)',
  gold: '#fff0cc',
  text: '#fff5d6',
  font: '"PingFang TC", "Microsoft JhengHei", sans-serif',
} as const; // 與主牌桌一致的 CSS 色票

const OVERLAY_CSS = `
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: none;
    align-items: center;
    justify-content: center;
    background: ${ORIENTATION_STYLE.background};
    box-shadow: inset 0 0 0 12px #0a3d2e, inset 0 0 0 14px ${ORIENTATION_STYLE.gold};
    color: ${ORIENTATION_STYLE.text};
    font-family: ${ORIENTATION_STYLE.font};
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    padding: 20px;
    user-select: none;
    -webkit-user-select: none;
  `; // 滿版方向遮罩
const PANEL_CSS = `padding: 42px 24px; border: 8px solid ${ORIENTATION_STYLE.gold};
    border-radius: 24px; background: #0a3d2e; width: min(280px, 80vw); box-sizing: border-box;`; // 綠色提示面板
const PHONE_CSS = `width: 88px; height: 48px; border: 3px solid ${ORIENTATION_STYLE.gold};
      border-radius: 10px; margin: 0 auto 28px; display: grid; place-items: center; font-size: 32px;`; // 橫向手機輪廓

const OVERLAY_ID = 'orientation-mask-overlay';

/** 是否目前為直式（高 > 寬）。 */
const isPortrait = (): boolean => window.innerHeight > window.innerWidth;

/**
 * 嘗試鎖定橫向。
 * iOS Safari 與部分桌機瀏覽器會拋例外或無聲忽略 — 都當作失敗處理（PRD §16.1）。
 */
const tryLockLandscape = async (): Promise<boolean> => {
  // screen.orientation.lock 在 TS DOM lib 標示為實驗 API；型別保守處理
  const orientation = (
    screen as unknown as {
      orientation?: { lock?: (orientation: string) => Promise<void> };
    }
  ).orientation;
  if (!orientation?.lock) return false;
  try {
    await orientation.lock('landscape');
    return true;
  } catch {
    return false;
  }
};

/**
 * 建立或取得 overlay DOM 元素。
 * 第一次呼叫時建立並掛到 document.body，之後重複呼叫會取得同一個。
 */
const ensureOverlay = (): HTMLDivElement => {
  let overlay = document.getElementById(OVERLAY_ID) as HTMLDivElement | null;
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.style.cssText = OVERLAY_CSS;
  const content = document.createElement('div');
  content.style.cssText = PANEL_CSS;
  content.innerHTML = `
    <div aria-hidden="true" style="${PHONE_CSS}">↻</div>
    <div>${ZH_TW.game.hint.rotateDevice}</div>
  `;
  overlay.appendChild(content);

  document.body.appendChild(overlay);
  return overlay;
};

/**
 * 啟用方向遮罩監聽。
 *
 * 呼叫時機：應在 main.ts 建立 Phaser.Game 之前 / 之後執行一次。
 * 之後遮罩會自動依方向變化開關顯示。
 */
export const installOrientationMask = (): void => {
  const overlay = ensureOverlay();

  const update = (): void => {
    overlay.style.display = isPortrait() ? 'flex' : 'none';
  };

  // 嘗試鎖定橫向（很可能會失敗，沒關係）
  void tryLockLandscape();

  // 初始化一次
  update();

  // 監聽變化
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
};
